import { Children } from "react";
import invariant from "invariant";
export var OPTIONAL = {
  min: 0,
  max: 1
};
export var OPTIONALS = {
  min: 0
};
export var REQUIRED = {
  min: 1,
  max: 1
};
export var REQUIREDS = {
  min: 1
};
var REST = "rest";
/**
 *
 *  Utility for generalized composition of React components.
 *
 * @param {React.Children} children
 * @param {Object} schema Hash containing `seapig` schema options
 *
 * @example <caption>Example usage of 🌊🐷</caption>
 *
 * const {
 *  iconChildren, //array of icon elements
 *  rest          // array of all other children
 * } = seapig(children, {
 *  icon: {
 *    min: 1,
 *    max: 1
 *  }
 * })
 *
 * @returns {Object} Object hash where each key is one of the given props concatenated with 'Children' and each value is a react component matching that prop.
 */

export default function seapig(children, schema) {
  var _propNames$reduce;

  if (schema === void 0) {
    schema = {};
  }

  invariant(schema && typeof schema === "object", "schema must be an object"); // extract schema and initialize the result

  var propNames = Object.keys(schema);
  var result = propNames.reduce(function (r, propName) {
    r[convertPropName(propName)] = [];
    return r;
  }, (_propNames$reduce = {}, _propNames$reduce[REST] = [], _propNames$reduce)); // build the result

  Children.forEach(children, function (child, i) {
    if (!child) {
      return;
    }

    var matchingPropNames = propNames.filter(function (p) {
      return child.props[p];
    });
    invariant(matchingPropNames.length <= 1, "expected at most 1 seapig prop per element but found " + matchingPropNames.length + " (" + matchingPropNames.join(', ') + ") for child at index " + i);
    var propName = matchingPropNames[0];
    result[propName ? convertPropName(propName) : REST].push(child);
  }); // validate the result

  propNames.forEach(function (propName) {
    assertSchemaProp("min", propName, result, schema);
    assertSchemaProp("max", propName, result, schema);
  });
  return result;
}

function convertPropName(propName) {
  return propName + "Children";
}

var defaultValidationSchema = {
  min: 0,
  max: Infinity
};

function isValidNum(num) {
  return typeof num === "number" && !Number.isNaN(num);
}

var validations = {
  min: function min(count, _min) {
    return count >= _min;
  },
  max: function max(count, _max) {
    return count <= _max;
  }
};

function assertSchemaProp(validationProp, schemaProp, result, schema) {
  var elementsCount = result[convertPropName(schemaProp)].length;
  var validationSchema = schema[schemaProp];
  var limit = isValidNum(validationSchema[validationProp]) ? validationSchema[validationProp] : defaultValidationSchema[validationProp];
  var invariantMessageEnding = limit + " `" + schemaProp + "` element" + (limit !== 1 ? "s" : "");
  invariant(validations[validationProp](elementsCount, limit), validationProp === "min" ? "Must have at least " + invariantMessageEnding : "Cannot have more than " + invariantMessageEnding);
}